import { 
    Box, 
    Typography, 
    Chip,
    Paper,
    alpha
} from '@mui/material';
import { useSelector } from 'react-redux';

// Иконки
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import VerifiedIcon from '@mui/icons-material/Verified';
import SpeedIcon from '@mui/icons-material/Speed';
import ScheduleIcon from '@mui/icons-material/Schedule';
import BuildIcon from '@mui/icons-material/Build';
import GavelIcon from '@mui/icons-material/Gavel';
import BlockIcon from '@mui/icons-material/Block';
import PendingIcon from '@mui/icons-material/Pending';
import CancelIcon from '@mui/icons-material/Cancel';

// Компонент для форматированного отображения расписания
const FormattedScheduleText = ({ text }) => {
    if (!text) return null;

    const lines = text.split('\n');

    return (
        <Box sx={{ whiteSpace: 'pre-line' }}>
            {lines.map((line, index) => {
                const isBold = line.startsWith('**') && line.endsWith('**');
                const cleanLine = isBold ? line.slice(2, -2) : line;

                return (
                    <Box
                        key={index}
                        component="span"
                        sx={{
                            fontWeight: isBold ? 600 : 'normal',
                            color: isBold ? '#f5bb3a' : 'inherit',
                            display: 'block'
                        }}
                    >
                        {cleanLine}
                    </Box>
                );
            })}
        </Box>
    );
};

// Функция для валидации даты сертификата поверки
const validatePoverkaDate = (dateString) => {
    if (!dateString) return { isValid: false, status: 'no-date' };

    try {
        const [day, month, year] = dateString.split('.');
        const date = new Date(year, month - 1, day);

        if (isNaN(date.getTime())) {
            return { isValid: false, status: 'invalid' };
        }

        const today = new Date();
        const diffTime = date.getTime() - today.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays < 0) {
            return { isValid: false, status: 'expired', daysLeft: diffDays };
        } else if (diffDays <= 30) {
            return { isValid: true, status: 'warning', daysLeft: diffDays };
        } else {
            return { isValid: true, status: 'valid', daysLeft: diffDays };
        }
    } catch (error) {
        return { isValid: false, status: 'invalid' };
    }
};

// Функция для валидации состояния
const validateStatus = (statusText) => {
    if (!statusText) return { isValid: false, status: 'no-data' };

    const lowerStatus = statusText.toLowerCase();
    if (lowerStatus.includes('утвержден')) {
        return { isValid: true, status: 'approved' };
    } else {
        return { isValid: false, status: 'not-approved' };
    }
};

// Функция для валидации работоспособности
const validatePerformance = (performanceText) => {
    if (!performanceText) return { isValid: false, status: 'no-data' };

    if (performanceText.trim() === 'Работает без ограничений') {
        return { isValid: true, status: 'working' };
    } else {
        return { isValid: false, status: 'not-working' };
    }
};

// Функция для форматирования расписания
const formatSchedule = (scheduleText) => {
    if (!scheduleText || scheduleText.trim() === '') {
        return { formatted: null, isCurrent: false };
    }

    try {
        const records = scheduleText.split('; ;').filter(record => record.trim() !== '');

        if (records.length === 0) {
            return { formatted: null, isCurrent: false };
        }

        const formattedRecords = [];
        const uniqueRecords = new Set();
        let hasCurrentRecord = false;

        records.forEach(record => {
            const trimmedRecord = record.trim();
            if (!trimmedRecord) return;

            const parts = trimmedRecord.split(';');
            if (parts.length < 2) return;

            const violationPart = parts[0].trim();
            const daysPart = parts[1].trim();

            const violationMatch = violationPart.match(/^(\d+\.\s*[^,]+),\s*(.+)$/);
            if (!violationMatch) return;

            const violationName = violationMatch[1].replace(/^\d+\.\s*/, '');
            const timeInfo = violationMatch[2];

            const daysMatch = daysPart.match(/Дни недели:\s*(.+)/);
            const days = daysMatch ? daysMatch[1].trim() : 'Не указаны';

            let timeRange = '';
            let isCurrent = false;

            if (timeInfo.includes(' - ')) {
                const timeMatch = timeInfo.match(/(\d{2}\.\d{2}\.\d{4}\s+\d{2}:\d{2})\s+-\s+(.+)/);
                if (timeMatch) {
                    const startTime = timeMatch[1];
                    const endTime = timeMatch[2];
                    timeRange = `${startTime} - ${endTime}`;

                    try {
                        const [startDate, startTimeOnly] = startTime.split(' ');
                        const [startDay, startMonth, startYear] = startDate.split('.');
                        const [startHours, startMinutes] = startTimeOnly.split(':');

                        const startDateTime = new Date(startYear, startMonth - 1, startDay, startHours, startMinutes);
                        const now = new Date();

                        if (startDateTime <= now) {
                            if (!endTime.includes('[*]')) {
                                const endTimeMatch = endTime.match(/(\d{2}\.\d{2}\.\d{4}\s+\d{2}:\d{2})/);
                                if (endTimeMatch) {
                                    const [endDate, endTimeOnly] = endTimeMatch[1].split(' ');
                                    const [endDay, endMonth, endYear] = endDate.split('.');
                                    const [endHours, endMinutes] = endTimeOnly.split(':');

                                    const endDateTime = new Date(endYear, endMonth - 1, endDay, endHours, endMinutes);

                                    if (now <= endDateTime) {
                                        isCurrent = true;
                                        hasCurrentRecord = true;
                                    }
                                } else {
                                    isCurrent = true;
                                    hasCurrentRecord = true;
                                }
                            } else {
                                isCurrent = true;
                                hasCurrentRecord = true;
                            }
                        }
                    } catch (e) {
                        // Игнорируем ошибки парсинга даты
                    }
                }
            } else if (timeInfo.includes('[*].[*].[*]')) {
                timeRange = 'Время не указано';
            } else {
                timeRange = timeInfo;
            }

            const uniqueKey = `${violationName}|${timeRange}|${days}`;

            if (!uniqueRecords.has(uniqueKey)) {
                uniqueRecords.add(uniqueKey);

                formattedRecords.push({
                    violation: violationName,
                    timeRange: timeRange,
                    days: days,
                    isCurrent: isCurrent
                });
            }
        });

        const formattedText = formattedRecords.map(record => {
            const violationText = record.isCurrent
                ? `**${record.violation}**`
                : record.violation;
            const timeText = record.isCurrent
                ? `**Время: ${record.timeRange}**`
                : `Время: ${record.timeRange}`;
            const daysText = record.isCurrent
                ? `**Дни: ${record.days}**`
                : `Дни: ${record.days}`;

            return `${violationText}\n${timeText}\n${daysText}`;
        }).join('\n\n');

        return {
            formatted: formattedText,
            isCurrent: hasCurrentRecord,
            recordCount: formattedRecords.length
        };

    } catch (error) {
        console.error('Ошибка форматирования расписания:', error);
        return { formatted: scheduleText, isCurrent: false };
    }
};

// Форматирование даты сертификата с дополнительной информацией
const formatPoverkaDate = (dateString) => {
    const validation = validatePoverkaDate(dateString);

    if (!validation.isValid && validation.status === 'no-date') {
        return { text: 'Нет данных', status: 'no-date' };
    }

    let statusText = '';
    switch (validation.status) {
        case 'expired':
            statusText = `(истёк ${Math.abs(validation.daysLeft)} дн. назад)`;
            break;
        case 'warning':
            statusText = `(осталось ${validation.daysLeft} дн.)`;
            break;
        case 'valid':
            statusText = `(ещё ${validation.daysLeft} дн.)`;
            break;
        default:
            statusText = '';
    }

    return { 
        text: `${dateString} ${statusText}`, 
        status: validation.status,
        daysLeft: validation.daysLeft
    };
};

const ComplexPautina = props => {
    const { host } = props;
    const isDarkMode = useSelector(state => state.settings.darkMode);

    const pautinaData = host.pautina?.data;
    const pautinaWarnings = host.pautina?.warnings;
    const pautinaTime = host.pautina?.time;

    // Цвета для статусов
    const statusColors = {
        success: isDarkMode ? '#3fb950' : '#2da44e',
        warning: '#f5bb3a',
        error: '#d63a23',
        info: isDarkMode ? '#58a6ff' : '#0969da'
    };

    if (!pautinaData) {
        return (
            <Box sx={{ 
                p: { xs: 1.5, md: 2 }, 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                height: '100%',
                backgroundColor: 'var(--background-default)'
            }}>
                <Paper
                    elevation={0}
                    sx={{
                        p: { xs: 2, md: 4 },
                        textAlign: 'center',
                        backgroundColor: 'var(--background-paper)',
                        borderRadius: { xs: '8px', md: '12px' },
                        border: '1px solid var(--border-default)',
                        maxWidth: { xs: '100%', md: 400 },
                    }}
                >
                    <InfoIcon sx={{ fontSize: { xs: 40, md: 56 }, color: 'var(--text-muted)', mb: { xs: 1.5, md: 2 }, opacity: 0.5 }} />
                    <Typography sx={{ fontSize: { xs: '0.875rem', md: '1rem' }, fontWeight: 500, color: 'var(--text-primary)', mb: 0.5 }}>
                        Нет данных из Паутины
                    </Typography>
                    <Typography sx={{ fontSize: { xs: '0.75rem', md: '0.85rem' }, color: 'var(--text-muted)' }}>
                        Данные для данного комплекса не найдены
                    </Typography>
                </Paper>
            </Box>
        );
    }

    const hasProblems = pautinaWarnings && (
        !pautinaWarnings.poverka || 
        pautinaWarnings.poverka_expiring ||
        !pautinaWarnings.status || 
        !pautinaWarnings.performance || 
        !pautinaWarnings.approved_vilations ||
        pautinaWarnings.schedule
    );

    const poverkaValidation = validatePoverkaDate(pautinaData.poverka_to);
    const statusValidation = validateStatus(pautinaData.status);
    const performanceValidation = validatePerformance(pautinaData.performance);
    const scheduleInfo = formatSchedule(pautinaData.schedule);
    const poverkaFormatted = formatPoverkaDate(pautinaData.poverka_to);

    // Рендер компактного алерта
    const renderAlert = (icon, title, message, color) => {
        const IconComponent = icon;
        return (
            <Box sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: { xs: 0.75, md: 1 },
                p: { xs: 0.75, md: 1 },
                backgroundColor: alpha(color, 0.08),
                border: `1px solid ${alpha(color, 0.3)}`,
                borderLeft: (theme) => ({ xs: `2px solid ${color}`, md: `3px solid ${color}` }),
                borderRadius: { xs: '6px', md: '8px' },
            }}>
                <IconComponent sx={{ fontSize: { xs: 16, md: 18 }, color, mt: 0.25, flexShrink: 0 }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: { xs: '0.75rem', md: '0.8rem' }, fontWeight: 600, color, lineHeight: 1.3 }}>
                        {title}
                    </Typography>
                    <Typography sx={{ fontSize: { xs: '0.65rem', md: '0.7rem' }, color: 'var(--text-muted)', lineHeight: 1.3, mt: 0.25 }}>
                        {message}
                    </Typography>
                </Box>
            </Box>
        );
    };

    // Рендер карточки данных
    const renderDataCard = (icon, title, value, color, description = null) => {
        const IconComponent = icon;
        const cardColor = color || statusColors.info;
        
        return (
            <Paper
                elevation={0}
                sx={{
                    p: { xs: 1, md: 1.25 },
                    borderRadius: { xs: '8px', md: '10px' },
                    backgroundColor: 'var(--background-paper)',
                    border: `1px solid ${alpha(cardColor, 0.3)}`,
                    transition: 'all 0.15s ease',
                    '&:hover': {
                        boxShadow: isDarkMode ? '0 4px 12px rgba(0,0,0,0.3)' : '0 4px 12px rgba(0,0,0,0.1)',
                        borderColor: cardColor
                    }
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: { xs: 0.75, md: 1 } }}>
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: { xs: 24, md: 28 },
                        height: { xs: 24, md: 28 },
                        borderRadius: { xs: '4px', md: '6px' },
                        backgroundColor: alpha(cardColor, 0.15),
                        color: cardColor,
                        flexShrink: 0
                    }}>
                        <IconComponent sx={{ fontSize: { xs: 14, md: 16 } }} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ 
                            fontSize: { xs: '0.6rem', md: '0.65rem' }, 
                            color: 'var(--text-muted)',
                            fontWeight: 500,
                            textTransform: 'uppercase',
                            letterSpacing: '0.3px'
                        }}>
                            {title}
                        </Typography>
                        <Typography sx={{ 
                            fontSize: { xs: '0.75rem', md: '0.85rem' },
                            fontWeight: 600,
                            color: value ? cardColor : 'var(--text-muted)',
                            lineHeight: 1.3,
                            mt: 0.25,
                            wordBreak: 'break-word',
                            whiteSpace: 'pre-line'
                        }}>
                            {value || 'Нет данных'}
                        </Typography>
                        {description && (
                            <Typography sx={{ 
                                fontSize: { xs: '0.65rem', md: '0.7rem' }, 
                                color: 'var(--text-muted)',
                                mt: 0.25
                            }}>
                                {description}
                            </Typography>
                        )}
                    </Box>
                </Box>
            </Paper>
        );
    };

    // Рендер карточки нарушений
    const renderViolationsCard = (icon, title, value, color) => {
        const IconComponent = icon;
        const hasValue = value && value.trim() !== '';
        const cardColor = color || 'var(--text-muted)';
        
        return (
            <Paper
                elevation={0}
                sx={{
                    borderRadius: { xs: '8px', md: '10px' },
                    backgroundColor: 'var(--background-paper)',
                    border: `1px solid ${hasValue ? alpha(cardColor, 0.3) : 'var(--border-default)'}`,
                    overflow: 'hidden',
                    transition: 'all 0.15s ease',
                    '&:hover': {
                        boxShadow: isDarkMode ? '0 4px 12px rgba(0,0,0,0.3)' : '0 4px 12px rgba(0,0,0,0.1)',
                    }
                }}
            >
                <Box sx={{
                    p: { xs: 0.75, md: 1 },
                    display: 'flex',
                    alignItems: 'center',
                    gap: { xs: 0.5, md: 0.75 },
                    backgroundColor: hasValue ? alpha(cardColor, 0.06) : 'var(--background-subtle)',
                    borderBottom: `1px solid ${hasValue ? alpha(cardColor, 0.2) : 'var(--border-default)'}`
                }}>
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: { xs: 20, md: 22 },
                        height: { xs: 20, md: 22 },
                        borderRadius: { xs: '4px', md: '5px' },
                        backgroundColor: hasValue ? cardColor : 'var(--text-muted)',
                        color: 'white',
                        flexShrink: 0
                    }}>
                        <IconComponent sx={{ fontSize: { xs: 10, md: 12 } }} />
                    </Box>
                    <Typography sx={{ 
                        fontSize: { xs: '0.7rem', md: '0.75rem' }, 
                        fontWeight: 600,
                        color: 'var(--text-primary)'
                    }}>
                        {title}
                    </Typography>
                </Box>
                <Box sx={{ p: { xs: 0.75, md: 1 } }}>
                    <Typography sx={{ 
                        fontSize: { xs: '0.7rem', md: '0.75rem' },
                        lineHeight: 1.4,
                        color: hasValue ? 'var(--text-primary)' : 'var(--text-muted)',
                        fontStyle: hasValue ? 'normal' : 'italic',
                        whiteSpace: 'pre-wrap'
                    }}>
                        {hasValue ? value : 'Нет данных'}
                    </Typography>
                </Box>
            </Paper>
        );
    };

    return (
        <Box sx={{ 
            p: { xs: 1, md: 1.5 }, 
            width: '100%', 
            height: '100%', 
            overflow: 'auto',
            backgroundColor: 'var(--background-default)',
            '&::-webkit-scrollbar': { width: { xs: '4px', md: '6px' } },
            '&::-webkit-scrollbar-track': { background: 'var(--background-subtle)', borderRadius: '3px' },
            '&::-webkit-scrollbar-thumb': { 
                background: 'var(--border-default)', 
                borderRadius: '3px',
                '&:hover': { background: 'var(--text-muted)' }
            }
        }}>
            {/* Заголовок */}
            <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: { xs: 1, md: 1.5 }, 
                mb: { xs: 1, md: 1.5 },
                p: { xs: 0.75, md: 1 },
                backgroundColor: 'var(--background-paper)',
                borderRadius: { xs: '8px', md: '10px' },
                border: '1px solid var(--border-default)',
                flexWrap: { xs: 'wrap', md: 'nowrap' }
            }}>
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: { xs: 28, md: 32 },
                    height: { xs: 28, md: 32 },
                    borderRadius: { xs: '6px', md: '8px' },
                    backgroundColor: statusColors.info,
                    color: 'white',
                    flexShrink: 0,
                    '& svg': { width: { xs: '16px', md: '18px' }, height: { xs: '16px', md: '18px' } }
                }}>
                    <svg viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg">
                        <path fill="currentColor" d="M20.45 18.484L77.59 90.588C74.865 139.18 53.842 176.38 25.2 210.62l-6.716-1.76v19.32l2.557.672c15.35 45.065 9.088 90.827 1.82 138.294l-4.376 2.047v20.63l9.18-4.294c6.516 10.707 13.658 27.047 19.06 41.33 4.283 11.33 7.474 20.984 9.253 26.535l-37.493 37.69v2.43h23.942l28.55-28.7c9.27-.363 41.77-1.473 82.7-.75 46.744.825 102.612 4.3 139.05 13.667l5.08 15.784h19.632l-7.43-23.088c35.94-41.51 71.912-80.525 131.734-102.34l51.774 20.486v-20.097l-42.983-17.008c-6.374-39.21 12.755-76.665 30.62-116.68l12.363-2.585v-19.092l-6.323 1.323-5.45-2.803C425.656 182.8 405.41 133.64 388.444 78.93l44.184-60.446h-23.15l-38.6 52.81c-58.273 1.254-112.475-2.463-154.446-37.79l-2.274-15.02h-18.9l2.838 18.75C170.96 66.224 140.532 85.188 93.512 80.59l-49.22-62.106H20.448z"/>
                    </svg>
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: { xs: '0.8rem', md: '0.9rem' }, fontWeight: 600, color: 'var(--text-primary)' }}>
                        Данные из Паутины
                    </Typography>
                    {pautinaTime && (
                        <Typography sx={{ fontSize: { xs: '0.65rem', md: '0.7rem' }, color: 'var(--text-muted)' }}>
                            Обновлено: {new Date(pautinaTime).toLocaleString('ru-RU')}
                        </Typography>
                    )}
                </Box>
                {hasProblems && (
                    <Chip
                        icon={<WarningIcon sx={{ fontSize: { xs: 12, md: 14 } }} />}
                        label="Есть проблемы"
                        size="small"
                        sx={{
                            height: { xs: 20, md: 24 },
                            fontSize: { xs: '0.65rem', md: '0.7rem' },
                            fontWeight: 600,
                            backgroundColor: alpha(statusColors.warning, 0.15),
                            color: statusColors.warning,
                            border: `1px solid ${alpha(statusColors.warning, 0.3)}`,
                            '& .MuiChip-icon': { color: statusColors.warning }
                        }}
                    />
                )}
            </Box>

            {/* Алерты о проблемах */}
            {hasProblems && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 0.5, md: 0.75 }, mb: { xs: 1, md: 1.5 } }}>
                    {!pautinaWarnings.poverka && renderAlert(
                        ErrorIcon,
                        'Сертификат поверки истёк',
                        poverkaFormatted.text,
                        statusColors.error
                    )}
                    {pautinaWarnings.poverka_expiring && renderAlert(
                        WarningIcon,
                        'Сертификат скоро истечёт',
                        poverkaFormatted.text,
                        statusColors.warning
                    )}
                    {!pautinaWarnings.status && renderAlert(
                        WarningIcon,
                        'Состояние не утверждено',
                        pautinaData.status || 'Не указано',
                        statusColors.warning
                    )}
                    {!pautinaWarnings.performance && renderAlert(
                        BuildIcon,
                        'Ограничения в работе',
                        `${pautinaData.performance || 'Нет данных'}${pautinaData.cause ? ` — ${pautinaData.cause}` : ''}`,
                        statusColors.warning
                    )}
                    {!pautinaWarnings.approved_vilations && renderAlert(
                        CancelIcon,
                        'Нет утверждённых нарушений',
                        'Список утверждённых нарушений пуст',
                        statusColors.warning
                    )}
                    {pautinaWarnings.schedule && renderAlert(
                        ScheduleIcon,
                        'Активное расписание ⏰',
                        'Есть активное ограничение по расписанию',
                        statusColors.info
                    )}
                </Box>
            )}

            {/* Основные данные */}
            <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
                gap: { xs: 0.75, md: 1 },
                mb: { xs: 1, md: 1.5 }
            }}>
                {renderDataCard(
                    VerifiedIcon,
                    'Сертификат поверки',
                    `От: ${pautinaData.poverka_from || '—'}\nДо: ${poverkaFormatted.text}`,
                    poverkaValidation.status === 'valid' ? statusColors.success : 
                        poverkaValidation.status === 'warning' ? statusColors.warning : 
                        poverkaValidation.status === 'expired' ? statusColors.error : null,
                    pautinaData.poverka ? `№ ${pautinaData.poverka}` : null
                )}
                {renderDataCard(
                    statusValidation.status === 'approved' ? CheckCircleIcon : WarningIcon,
                    'Состояние',
                    pautinaData.status,
                    statusValidation.status === 'approved' ? statusColors.success : statusColors.warning
                )}
                {renderDataCard(
                    performanceValidation.status === 'working' ? CheckCircleIcon : BuildIcon,
                    'Работоспособность',
                    pautinaData.performance,
                    performanceValidation.status === 'working' ? statusColors.success : statusColors.warning,
                    pautinaData.cause || null
                )}
                {renderDataCard(
                    SpeedIcon,
                    'Предел скорости',
                    pautinaData.speed ? `${pautinaData.speed} км/ч` : null,
                    statusColors.info
                )}
                {renderDataCard(
                    GavelIcon,
                    'Нарушения',
                    pautinaData.violations,
                    statusColors.info
                )}
            </Box>

            {/* Нарушения по категориям */}
            <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: { xs: 0.5, md: 0.75 }, 
                mb: { xs: 0.75, md: 1 },
                px: 0.5
            }}>
                <GavelIcon sx={{ fontSize: { xs: 14, md: 16 }, color: 'var(--text-muted)' }} />
                <Typography sx={{ fontSize: { xs: '0.75rem', md: '0.8rem' }, fontWeight: 600, color: 'var(--text-primary)' }}>
                    Нарушения по категориям
                </Typography>
            </Box>
            <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                gap: { xs: 0.75, md: 1 },
                mb: { xs: 1, md: 1.5 }
            }}>
                {renderViolationsCard(CheckCircleIcon, 'Утверждённые', pautinaData.approved_vilations, statusColors.success)}
                {renderViolationsCard(PendingIcon, 'Предложенные', pautinaData.proposed_vilations, statusColors.info)}
                {renderViolationsCard(CancelIcon, 'Отклонённые', pautinaData.rejected_vilations, statusColors.error)}
                {renderViolationsCard(BlockIcon, 'Исключённые', pautinaData.excluded_violations, statusColors.warning)}
            </Box>

            {/* Расписание */}
            {scheduleInfo.formatted && (
                <>
                    <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: { xs: 0.5, md: 0.75 }, 
                        mb: { xs: 0.75, md: 1 },
                        px: 0.5,
                        flexWrap: 'wrap'
                    }}>
                        <ScheduleIcon sx={{ fontSize: { xs: 14, md: 16 }, color: 'var(--text-muted)' }} />
                        <Typography sx={{ fontSize: { xs: '0.75rem', md: '0.8rem' }, fontWeight: 600, color: 'var(--text-primary)' }}>
                            Расписание
                        </Typography>
                        {scheduleInfo.isCurrent && (
                            <Chip
                                label="⏰ Активно"
                                size="small"
                                sx={{
                                    height: { xs: 16, md: 18 },
                                    fontSize: { xs: '0.55rem', md: '0.6rem' },
                                    fontWeight: 600,
                                    backgroundColor: alpha(statusColors.warning, 0.15),
                                    color: statusColors.warning,
                                    border: `1px solid ${alpha(statusColors.warning, 0.3)}`
                                }}
                            />
                        )}
                    </Box>
                    <Paper
                        elevation={0}
                        sx={{
                            p: { xs: 1, md: 1.25 },
                            borderRadius: { xs: '8px', md: '10px' },
                            backgroundColor: scheduleInfo.isCurrent ? alpha(statusColors.warning, 0.06) : 'var(--background-paper)',
                            border: `1px solid ${scheduleInfo.isCurrent ? alpha(statusColors.warning, 0.3) : 'var(--border-default)'}`,
                            borderLeft: (theme) => ({ 
                                xs: `2px solid ${scheduleInfo.isCurrent ? statusColors.warning : 'var(--border-default)'}`, 
                                md: `3px solid ${scheduleInfo.isCurrent ? statusColors.warning : 'var(--border-default)'}` 
                            }),
                        }}
                    >
                        <Typography sx={{ fontSize: { xs: '0.7rem', md: '0.75rem' }, lineHeight: 1.5, color: 'var(--text-primary)' }}>
                            <FormattedScheduleText text={scheduleInfo.formatted} />
                        </Typography>
                    </Paper>
                </>
            )}
        </Box>
    );
};

export default ComplexPautina;
